import { AUDIO_LABELS, readAudioClips, readBackgroundAudio, type SavedAudio } from './AudioLibrary';
import { AudioSystem, validateBackgroundAudioFile } from './AudioSystem';

export class AudioEditor {
  private readonly select = document.querySelector<HTMLSelectElement>('#audio-choice')!;
  private readonly file = document.querySelector<HTMLInputElement>('#audio-file')!;
  private readonly status = document.querySelector<HTMLElement>('#audio-status')!;
  private readonly name = document.querySelector<HTMLElement>('#audio-name')!;
  private readonly play = document.querySelector<HTMLButtonElement>('#audio-play')!;
  private readonly stop = document.querySelector<HTMLButtonElement>('#audio-stop')!;
  private readonly reset = document.querySelector<HTMLButtonElement>('#audio-reset')!;
  private readonly oracleStatus = document.querySelector<HTMLElement>('#audio-oracle-status')!;
  private readonly oracleUpload = document.querySelector<HTMLButtonElement>('#audio-oracle-upload-button')!;
  private readonly oracleProgress = document.querySelector<HTMLProgressElement>('#audio-oracle-progress')!;
  private readonly oraclePercent = document.querySelector<HTMLOutputElement>('#audio-oracle-percent')!;
  private readonly oraclePath = document.querySelector<HTMLAnchorElement>('#audio-oracle-path')!;
  private readonly backgroundFile = document.querySelector<HTMLInputElement>('#audio-background-file')!;
  private readonly backgroundUpload = document.querySelector<HTMLButtonElement>('#audio-background-upload')!;
  private readonly backgroundName = document.querySelector<HTMLElement>('#audio-background-name')!;
  private readonly backgroundStatus = document.querySelector<HTMLElement>('#audio-background-status')!;
  private readonly backgroundProgress = document.querySelector<HTMLProgressElement>('#audio-background-progress')!;
  private readonly backgroundPercent = document.querySelector<HTMLOutputElement>('#audio-background-percent')!;
  private readonly backgroundPath = document.querySelector<HTMLAnchorElement>('#audio-background-path')!;
  private readonly backgroundNoPath = document.querySelector<HTMLElement>('#audio-background-no-path')!;
  private readonly backgroundLegacyUpload = document.querySelector<HTMLButtonElement>('#audio-background-upload-legacy')!;
  private readonly backgroundPlay = document.querySelector<HTMLButtonElement>('#audio-background-play')!;
  private readonly backgroundStop = document.querySelector<HTMLButtonElement>('#audio-background-stop')!;
  private readonly backgroundReset = document.querySelector<HTMLButtonElement>('#audio-background-reset')!;
  private readonly events = new AbortController();
  private readonly names = new Map<string, string>();
  private readonly remoteNames = new Map<string, string>();
  private previewAudio: AudioSystem | null = null;
  private previewVersion = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private busy = false;
  private backgroundBusy = false;
  private selectedBackground: File | null = null;
  private selectedClip: { id: string; file: File } | null = null;
  private oracleBusy = false;
  private legacyBackground: SavedAudio | null = null;
  private remoteBackground = false;
  private backgroundPreview: HTMLAudioElement | null = null;
  private backgroundPreviewUrl: string | null = null;

  constructor(private readonly audio: AudioSystem, private readonly getCreatorToken: () => string) {
    this.backgroundPlay.disabled = this.backgroundStop.disabled = this.backgroundReset.disabled = this.backgroundUpload.disabled = this.backgroundLegacyUpload.disabled = true;
    for (const [id, label] of Object.entries(AUDIO_LABELS)) this.select.add(new Option(label, id));
    const options = { signal: this.events.signal };
    this.select.addEventListener('change', () => {
      this.stopPreview();
      this.selectedClip = null;
      this.file.value = '';
      this.oracleProgress.hidden = this.oraclePercent.hidden = true;
      this.refresh();
      void this.refreshOracleClip();
    }, options);
    this.play.addEventListener('click', () => { void this.listen(); }, options);
    this.stop.addEventListener('click', () => this.stopPreview(), options);
    this.file.addEventListener('change', () => {
      const file = this.file.files?.[0];
      this.selectedClip = file ? { id: this.select.value, file } : null;
      this.refresh();
      if (file) void this.change(file);
    }, options);
    this.reset.addEventListener('click', () => {
      this.selectedClip = null;
      this.file.value = '';
      this.refresh();
      void this.change(null);
    }, options);
    this.oracleUpload.addEventListener('click', () => { void this.uploadClip(); }, options);
    this.backgroundFile.addEventListener('change', () => {
      this.selectedBackground = this.backgroundFile.files?.[0] ?? null;
      this.backgroundUpload.disabled = !this.selectedBackground || this.backgroundBusy;
      this.backgroundProgress.hidden = true;
      this.backgroundPercent.hidden = true;
      this.backgroundStatus.textContent = this.selectedBackground ? 'MP3 listo para subir a Oracle.' : '';
    }, options);
    this.backgroundUpload.addEventListener('click', () => { if (this.selectedBackground) void this.uploadBackground(this.selectedBackground); }, options);
    this.backgroundLegacyUpload.addEventListener('click', () => {
      if (this.legacyBackground) void this.uploadBackground(new File([this.legacyBackground.blob], this.legacyBackground.name, { type: 'audio/mpeg' }));
    }, options);
    this.backgroundPlay.addEventListener('click', () => { void this.listenBackground(); }, options);
    this.backgroundStop.addEventListener('click', () => { this.stopBackgroundPreview(); this.backgroundStatus.textContent = 'Vista previa detenida.'; }, options);
    this.backgroundReset.addEventListener('click', () => { void this.removeBackground(); }, options);
    void readAudioClips().then(clips => {
      for (const clip of clips) this.names.set(clip.id, clip.name);
      this.refresh();
    }).catch(() => { this.status.textContent = 'El almacenamiento de audios no está disponible en este navegador.'; });
    void Promise.all([this.audio.refreshBackground(), readBackgroundAudio()]).then(([name, localClip]) => {
      this.remoteBackground = !!name;
      this.legacyBackground = localClip;
      this.backgroundName.textContent = name || (localClip ? `Pendiente de subir desde este navegador: ${localClip.name}` : 'Sin pista en Oracle');
      this.setBackgroundPath(!!name);
      this.backgroundProgress.hidden = !name;
      this.backgroundProgress.value = name ? 100 : 0;
      this.backgroundPercent.hidden = !name;
      this.backgroundPercent.textContent = name ? '100%' : '';
      this.backgroundStatus.textContent = name ? 'Pista subida correctamente y disponible en Oracle.' : 'No hay una pista subida a Oracle.';
      this.backgroundPlay.disabled = this.backgroundStop.disabled = this.backgroundReset.disabled = !name;
      this.backgroundLegacyUpload.hidden = !localClip;
      this.backgroundLegacyUpload.disabled = !localClip;
    }).catch(() => { this.backgroundName.textContent = 'No se pudo consultar Oracle.'; this.backgroundStatus.textContent = 'Comprueba la conexión y vuelve a abrir Opciones.'; });
    this.refresh();
    void this.refreshOracleClip();
  }

  private refresh(): void {
    this.name.textContent = this.selectedClip?.file.name ?? this.remoteNames.get(this.select.value) ?? this.names.get(this.select.value) ?? 'Audio original';
    this.select.disabled = this.file.disabled = this.reset.disabled = this.play.disabled = this.busy || this.oracleBusy;
    this.oracleUpload.disabled = this.busy || this.oracleBusy || !this.selectedClip || this.selectedClip.id !== this.select.value;
  }

  private async change(file: File | null): Promise<void> {
    if (this.busy) return;
    this.stopPreview();
    const id = this.select.value;
    this.busy = true;
    this.refresh();
    this.status.textContent = 'Guardando audio…';
    try {
      await this.audio.replace(id, file);
      if (file) this.names.set(id, file.name);
      else this.names.delete(id);
      this.status.textContent = file
        ? 'MP3 guardado y aplicado aquí. Pulsa «Subir MP3 a Oracle» para compartirlo en todos los dispositivos.'
        : 'Reemplazo local quitado. Se usa el audio de Oracle o el original.';
    } catch (error) {
      if (this.selectedClip?.id === id && this.selectedClip.file === file) this.selectedClip = null;
      this.status.textContent = error instanceof Error ? error.message : 'No se pudo guardar el audio. Se conserva el anterior.';
    } finally { this.busy = false; this.refresh(); }
  }

  private async refreshOracleClip(): Promise<void> {
    const id = this.select.value;
    this.oracleStatus.textContent = `Consultando Oracle para «${AUDIO_LABELS[id]}»…`;
    this.oraclePath.hidden = true;
    try {
      const info = await this.audio.clipInfo(id);
      if (this.select.value !== id) return;
      if (info.available) {
        this.remoteNames.set(id, info.name ?? 'MP3 de Oracle');
        const path = new URL(`${import.meta.env.BASE_URL}api/audio/clips/${encodeURIComponent(id)}`, window.location.href).href;
        this.oracleStatus.textContent = `Audio disponible en Oracle: ${info.name ?? 'MP3 personalizado'}.`;
        this.refresh();
        this.oraclePath.href = path;
        this.oraclePath.textContent = path;
        this.oraclePath.hidden = false;
      } else {
        this.remoteNames.delete(id);
        this.oracleStatus.textContent = 'Sin MP3 global; se usa el audio original del juego. Selecciona un archivo y súbelo para compartirlo.';
        this.refresh();
      }
    } catch {
      if (this.select.value === id) this.oracleStatus.textContent = 'No se pudo consultar Oracle; puedes intentar subir el MP3.';
    }
  }

  private async uploadClip(): Promise<void> {
    const selected = this.selectedClip;
    if (!selected || selected.id !== this.select.value || this.oracleBusy) return;
    this.oracleBusy = true;
    this.oracleProgress.hidden = false;
    this.oracleProgress.value = 0;
    this.oraclePercent.hidden = false;
    this.oraclePercent.textContent = '0%';
    this.oracleStatus.textContent = 'Subiendo MP3 a Oracle…';
    this.refresh();
    try {
      await this.audio.uploadClip(selected.id, selected.file, this.getCreatorToken(), percent => {
        this.oracleProgress.value = percent;
        this.oraclePercent.textContent = `${percent}%`;
      });
      this.remoteNames.set(selected.id, selected.file.name);
      this.names.delete(selected.id);
      this.selectedClip = null;
      this.file.value = '';
      const path = new URL(`${import.meta.env.BASE_URL}api/audio/clips/${encodeURIComponent(selected.id)}`, window.location.href).href;
      this.oracleStatus.textContent = `Subida correcta. «${AUDIO_LABELS[selected.id]}» está disponible en todos los dispositivos.`;
      this.oraclePath.href = path;
      this.oraclePath.textContent = path;
      this.oraclePath.hidden = false;
    } catch (error) {
      this.oracleStatus.textContent = error instanceof Error ? error.message : 'No se pudo subir el MP3 a Oracle.';
    } finally {
      this.oracleBusy = false;
      this.refresh();
    }
  }

  private async listen(): Promise<void> {
    this.stopPreview();
    const version = this.previewVersion;
    const preview = this.previewAudio = new AudioSystem();
    this.status.textContent = 'Cargando vista previa…';
    try {
      await preview.prepare();
      if (version !== this.previewVersion) return;
      if (this.selectedClip?.id === this.select.value) await preview.replace(this.select.value, this.selectedClip.file);
      if (version !== this.previewVersion) return;
      preview.preview(this.select.value);
      this.status.textContent = `Escuchando: ${AUDIO_LABELS[this.select.value]}`;
      if (this.select.value !== 'endMusic' && this.select.value !== 'menuMusic') this.timer = setTimeout(() => this.stopPreview(), 31_000);
    } catch {
      if (version === this.previewVersion) { this.stopPreview(); this.status.textContent = 'No se pudo reproducir el audio.'; }
    }
  }

  private async uploadBackground(file: File): Promise<void> {
    if (this.backgroundBusy) return;
    try { validateBackgroundAudioFile(file); }
    catch (error) {
      this.backgroundProgress.hidden = true;
      this.backgroundPercent.hidden = true;
      this.backgroundStatus.textContent = error instanceof Error ? error.message : 'El archivo no es válido.';
      return;
    }
    this.stopBackgroundPreview();
    this.backgroundBusy = true;
    this.setBackgroundControlsDisabled(true);
    this.backgroundProgress.hidden = false;
    this.backgroundProgress.value = 0;
    this.backgroundPercent.hidden = false;
    this.backgroundPercent.textContent = '0%';
    this.backgroundUpload.textContent = 'Subiendo a Oracle… 0%';
    this.backgroundStatus.textContent = 'Conectando con Oracle…';
    try {
      await this.audio.replaceBackground(file, this.getCreatorToken(), percent => {
        this.backgroundProgress.value = percent;
        this.backgroundPercent.textContent = `${percent}%`;
        this.backgroundUpload.textContent = `Subiendo a Oracle… ${percent}%`;
        this.backgroundStatus.textContent = `Subiendo a Oracle… ${percent}%`;
      });
      this.remoteBackground = true;
      this.legacyBackground = null;
      this.selectedBackground = null;
      this.backgroundName.textContent = this.audio.getBackgroundName();
      this.setBackgroundPath(true);
      this.backgroundLegacyUpload.hidden = true;
      this.backgroundProgress.value = 100;
      this.backgroundPercent.textContent = '100%';
      this.backgroundStatus.textContent = 'Subida correcta. MP3 guardado en Oracle y disponible durante los partidos.';
    } catch (error) {
      if (this.backgroundProgress.value === 0) {
        this.backgroundProgress.hidden = true;
        this.backgroundPercent.hidden = true;
      }
      this.backgroundStatus.textContent = error instanceof Error ? error.message : 'No se pudo guardar la pista.';
    } finally {
      this.backgroundBusy = false;
      this.setBackgroundControlsDisabled(false);
      this.backgroundUpload.disabled = !this.selectedBackground;
      this.backgroundUpload.textContent = 'Subir pista a Oracle';
    }
  }

  private async removeBackground(): Promise<void> {
    if (this.backgroundBusy || !this.remoteBackground) return;
    this.stopBackgroundPreview();
    this.backgroundBusy = true;
    this.setBackgroundControlsDisabled(true);
    this.backgroundProgress.hidden = true;
    this.backgroundPercent.hidden = true;
    this.backgroundPercent.textContent = '';
    this.backgroundStatus.textContent = 'Quitando pista de Oracle…';
    try {
      await this.audio.replaceBackground(null, this.getCreatorToken(), () => undefined);
      this.remoteBackground = false;
      this.backgroundName.textContent = 'Sin pista en Oracle';
      this.setBackgroundPath(false);
      this.backgroundStatus.textContent = 'Pista quitada de Oracle.';
    } catch (error) {
      this.backgroundStatus.textContent = error instanceof Error ? error.message : 'No se pudo quitar la pista de Oracle.';
    } finally { this.backgroundBusy = false; this.setBackgroundControlsDisabled(false); }
  }

  private setBackgroundControlsDisabled(disabled: boolean): void {
    this.backgroundFile.disabled = disabled;
    this.backgroundUpload.disabled = disabled || !this.selectedBackground;
    this.backgroundPlay.disabled = this.backgroundStop.disabled = this.backgroundReset.disabled = disabled || !this.remoteBackground;
    this.backgroundLegacyUpload.disabled = disabled || !this.legacyBackground;
  }

  private setBackgroundPath(available: boolean): void {
    this.backgroundPath.href = new URL(`${import.meta.env.BASE_URL}api/audio/background`, window.location.href).href;
    this.backgroundPath.textContent = this.backgroundPath.href;
    this.backgroundPath.hidden = !available;
    this.backgroundNoPath.hidden = available;
  }

  private async listenBackground(): Promise<void> {
    this.stopBackgroundPreview();
    try {
      const name = await this.audio.refreshBackground();
      if (!name) { this.backgroundStatus.textContent = 'No hay una pista guardada en Oracle.'; return; }
      const url = this.backgroundPreviewUrl = `${import.meta.env.BASE_URL}api/audio/background?preview=${Date.now()}`;
      const preview = this.backgroundPreview = new Audio(url);
      preview.volume = 0.5;
      preview.onended = () => { this.stopBackgroundPreview(); this.backgroundStatus.textContent = 'Vista previa finalizada.'; };
      await preview.play();
      this.backgroundStatus.textContent = `Escuchando desde Oracle: ${name}`;
    } catch {
      this.stopBackgroundPreview();
      this.backgroundStatus.textContent = 'No se pudo reproducir la pista.';
    }
  }

  private stopBackgroundPreview(): void {
    if (this.backgroundPreview) {
      this.backgroundPreview.pause();
      this.backgroundPreview.removeAttribute('src');
      this.backgroundPreview.load();
      this.backgroundPreview = null;
    }
    if (this.backgroundPreviewUrl) URL.revokeObjectURL(this.backgroundPreviewUrl);
    this.backgroundPreviewUrl = null;
  }

  stopPreview(): void {
    this.previewVersion++;
    clearTimeout(this.timer);
    this.previewAudio?.dispose();
    this.previewAudio = null;
  }

  dispose(): void { this.stopPreview(); this.stopBackgroundPreview(); this.events.abort(); }
}
